import { useMemo } from 'react';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import settingsParams, {
  DisplayContent,
  ItemAppearance,
  LayoutType,
  OpenQuestionIcon,
  Spacing,
} from '../components/FaqOoi/settingsParams';
import { ISettingsContextValue } from '@wix/tpa-settings';
import stylesParams, {
  convertItemAppearanceStyleToItemAppearance,
} from '../components/FaqOoi/stylesParams';
import { PresetSettings } from '../types/designTypes';
import { CategoriesPosition, ItemAppearanceStyle } from '../enums';
import { presetToLayoutMap } from './presetToLayoutMap';
import constants from '../constants';

export type UseLayoutTypeResult = {
  layout: {
    numberLayout: boolean;
    accordionLayout: boolean;
    listLayout: boolean;
    layoutValue: LayoutType;
  };
  categoriesPosition: CategoriesPosition;
  itemAppearance: ItemAppearance;
  displayContent: DisplayContent;
  openQuestionIcon: OpenQuestionIcon;
  spacing: Spacing;
  showTitle: boolean;
};

export const useLayoutType = (_isEditor3?: boolean): UseLayoutTypeResult => {
  const settings: ISettingsContextValue = useSettings();
  const styles = useStyles();

  let __isEditor3 = settings.get(settingsParams.isEditor3) ?? false;
  __isEditor3 = styles.get(stylesParams.isEditor3) ?? _isEditor3;
  const isEditor3 = _isEditor3 ?? __isEditor3;

  let layout: LayoutType =
    settings.get(settingsParams.layout) ?? LayoutType.Collapse;
  let categoriesPosition: CategoriesPosition =
    settings.get(settingsParams.categoriesPosition) ?? CategoriesPosition.TOP;
  let itemAppearance: ItemAppearance =
    settings.get(settingsParams.itemAppearance) ?? ItemAppearance.BOX;
  let displayContent: DisplayContent =
    settings.get(settingsParams.displayContent) ??
    constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
  const openQuestionIcon: OpenQuestionIcon =
    settings.get(settingsParams.openQuestionIcon) ?? OpenQuestionIcon.ARROW;
  const defaultSpacing: Record<keyof Spacing, number> = settings.get(
    settingsParams.spacing,
  ) ?? {
    gap: 12,
    betweencols: 12,
    vertical: 32,
    horizontal: 32,
  };
  let gap = defaultSpacing.gap;
  let vertical = defaultSpacing.vertical;
  let horizontal = defaultSpacing.horizontal;
  const betweencols = defaultSpacing.betweencols;

  if (isEditor3) {
    const presetSettings =
      (styles.get?.(stylesParams.presetSettings) as PresetSettings) ??
      PresetSettings.ACCORDION_TOP_CATEGORIES;
    const mappedPreset = presetToLayoutMap[presetSettings];
    layout = mappedPreset.layout ?? LayoutType.Collapse;
    categoriesPosition =
      mappedPreset.categoriesPosition ?? CategoriesPosition.TOP;
    displayContent =
      styles.get(stylesParams.showFAQCategories) ??
      constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
    if (displayContent === true) {
      displayContent = constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES;
    }
    const itemAppearanceStyle =
      styles.get(stylesParams.itemAppearanceStyle) ?? ItemAppearanceStyle.BOX;
    itemAppearance = convertItemAppearanceStyleToItemAppearance(
      itemAppearanceStyle,
    ) as ItemAppearance;
    gap = styles.get(stylesParams.spacingGap) ?? gap;
    vertical = styles.get(stylesParams.spacingVertical) ?? vertical;
    horizontal = styles.get(stylesParams.spacingHorizontal) ?? horizontal;
  }

  const titleSettings = settings.get(settingsParams.title);
  const showTitleStyleParam = styles.get?.(stylesParams.showTitle) ?? true;
  const showTitle = showTitleStyleParam && (titleSettings?.show ?? true);

  return useMemo<UseLayoutTypeResult>(
    () => ({
      layout: {
        numberLayout: layout === LayoutType.Stripes,
        accordionLayout: layout === LayoutType.Collapse,
        listLayout: layout === LayoutType.Basic,
        layoutValue: layout,
      },
      categoriesPosition,
      itemAppearance,
      displayContent,
      openQuestionIcon,
      spacing: { gap, betweencols, vertical, horizontal },
      showTitle,
    }),
    [
      layout,
      categoriesPosition,
      itemAppearance,
      displayContent,
      openQuestionIcon,
      gap,
      betweencols,
      vertical,
      horizontal,
      showTitle,
    ],
  );
};
